import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Zap, TrendingUp, ArrowRight, Info } from "lucide-react";
import { siSample } from "@/lib/api";

/** Sample report — spec §19 requires "DEMONSTRATION DATA" ribbon on every
 *  page containing sample metrics so it is never confused with a real audit. */
const TONES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
};
const tone = (v) => (v >= 75 ? "emerald" : v >= 50 ? "amber" : "rose");

const Score = ({ label, v }) => {
  const c = tone(v);
  return (
    <div className={`rounded-2xl border p-5 ${TONES[c]}`} data-testid={`si-sample-score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-2 font-heading text-4xl font-black tabular-nums">{v}<span className="text-lg opacity-60">/100</span></div>
    </div>
  );
};

export default function SearchIntelligenceSample() {
  const [d, setD] = useState(null);
  useEffect(() => { siSample().then(setD); }, []);
  if (!d) return (
    <div className="min-h-screen bg-white"><Navbar /><div className="p-10 text-slate-500">Loading sample…</div><Footer /></div>
  );
  return (
    <div className="min-h-screen bg-white" data-testid="si-sample">
      {/* Loud, un-missable demo ribbon — pinned above the fixed navbar */}
      <div className="fixed left-0 right-0 top-0 z-[60] border-b-2 border-amber-400 bg-amber-100 py-2.5 text-center text-xs font-bold uppercase tracking-widest text-amber-900" data-testid="si-sample-ribbon">
        <Info className="mr-1.5 inline h-3.5 w-3.5" /> {d.demo_ribbon}
      </div>
      <div className="h-10" />{/* spacer for the fixed ribbon */}
      <Navbar />

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            Sample report · Methodology v{d.scores.methodology_version}
          </div>
          <h1 className="mt-3 font-heading text-3xl font-black text-slate-900 sm:text-4xl">
            {d.site.label}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500 font-mono">{d.site.url}</p>
          <p className="mt-4 text-slate-600">
            A realistic Search Intelligence report — every field is illustrative demonstration data.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Score label="Readiness" v={d.scores.readiness} />
          <Score label="Technical Health" v={d.scores.technical_health} />
          <Score label="Search Opportunity" v={d.scores.search_opportunity} />
          <Score label="Business Alignment" v={d.scores.business_alignment} />
          <Score label="Data Confidence" v={d.scores.data_confidence} />
        </div>
        <div className="mt-3 text-sm text-slate-500">
          Confidence label: <b className="text-slate-800">{d.scores.confidence_label}</b>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-4">
        <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-rose-700">
          <AlertTriangle className="h-5 w-5" /> Critical
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {d.critical.map((c, i) => (
            <Card key={i} className="border-rose-200 bg-rose-50 p-4" data-testid={`si-sample-crit-${i}`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-rose-800">
                <Badge variant="outline">{c.code.replace(/_/g, " ")}</Badge>
                <span className="font-mono text-xs">{c.url}</span>
              </div>
              <p className="mt-2 text-sm text-rose-900">{c.evidence}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-6">
        <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-amber-700">
          <Zap className="h-5 w-5" /> Quick wins
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {d.quick_wins.map((c, i) => (
            <Card key={i} className="border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <Badge variant="outline">{c.code.replace(/_/g, " ")}</Badge>
                <span className="text-xs font-mono">{c.count} pages</span>
              </div>
              <p className="mt-2 text-sm text-amber-900">{c.evidence}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-6">
        <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-sky-700">
          <TrendingUp className="h-5 w-5" /> Opportunities
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {d.opportunities.map((c, i) => (
            <Card key={i} className="border-sky-200 bg-sky-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-900">
                <Badge variant="outline">{c.code.replace(/_/g, " ")}</Badge>
                <span className="text-xs font-mono">{c.count} pages</span>
              </div>
              <p className="mt-2 text-sm text-sky-900">{c.evidence}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Next actions</h2>
        <ol className="mt-4 space-y-2 pl-5 list-decimal text-slate-700">
          {d.next_actions.map((n, i) => <li key={i}>{n}</li>)}
        </ol>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="bg-orange-600 text-white hover:bg-orange-700" data-testid="si-sample-cta-run">
            <Link to="/si">Run this on my site <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" data-testid="si-sample-cta-method">
            <Link to="/si/methodology">See scoring methodology</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
